import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Avatar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Person,
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useUserStore } from '../../stores/userStore';
import { DataTable } from '../../components/admin/DataTable';
import { FormDialog } from '../../components/admin/FormDialog';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { StatusChip } from '../../components/admin/StatusChip';
import { FilterBar } from '../../components/admin/FilterBar';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/api';
import { User } from '../../types';
import { getAvatarUrl, isDefaultAvatar } from '../../utils/avatar';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';

export const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    users,
    loading,
    error,
    filters,
    setUsers,
    setLoading,
    setError,
    setFilters,
    addUser,
    updateUser,
    removeUser,
  } = useUserStore();
  const { user: currentUser } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; userId: number | null }>({
    open: false,
    userId: null,
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    skill_level: 3,
    elo_rating: undefined as number | undefined,
    preferred_side: 'right',
    is_active: true,
    is_admin: false,
    club_id: undefined as number | undefined,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [clubs, setClubs] = useState<any[]>([]);

  useEffect(() => {
    if (currentUser?.is_admin) {
      loadUsers();
      loadClubs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const editUserId = (location.state as { editUserId?: number })?.editUserId;
  useEffect(() => {
    if (!editUserId || loading) return;
    const arr = Array.isArray(users) ? users : [];
    const u = arr.find((x) => x.id === editUserId);
    if (u) {
      handleEdit(u);
      navigate('/admin/users', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUserId, users, loading]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllUsers();
      // Ensure response.data is always an array
      const data: any = response?.data;
      const usersArray = Array.isArray(data) ? data : (Array.isArray(data?.users) ? data.users : (Array.isArray(data?.data) ? data.data : []));
      setUsers(usersArray);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to load users';
      setError(errorMessage);
      setUsers([]); // Always set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadClubs = async () => {
    try {
      const response = await apiService.getClubs();
      // Ensure response.data is always an array
      const data: any = response.data;
      const clubs = Array.isArray(data) ? data : (data?.clubs || data?.data || []);
      setClubs(Array.isArray(clubs) ? clubs.filter((club: any) => club.is_active) : []);
    } catch (error) {
      console.error('Failed to load clubs:', error);
      setClubs([]); // Set empty array on error
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!editingUser && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    if (formData.skill_level < 1 || formData.skill_level > 10) {
      newErrors.skill_level = 'Skill level must be between 1 and 10';
    }
    if (editingUser && formData.elo_rating != null && (formData.elo_rating < 1 || formData.elo_rating > 10)) {
      newErrors.elo_rating = 'ELO rating must be between 1.0 and 10.0';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (editingUser) {
        const updateData: any = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        if (formData.elo_rating != null) {
          updateData.elo_rating = formData.elo_rating;
        }
        const response = await apiService.updateUser(editingUser.id, updateData);
        updateUser(response.data);
      } else {
        const response = await apiService.createUser(formData);
        addUser(response.data);
      }
      setOpenDialog(false);
      resetForm();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEdit = (user: User) => {
    const effectiveElo = (user as any).elo_rating ?? (user as any).initial_elo ?? 5;
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      skill_level: user.skill_level || 3,
      elo_rating: effectiveElo,
      preferred_side: user.preferred_side || 'right',
      is_active: user.is_active,
      is_admin: user.is_admin,
      club_id: (user as any).club_id,
    });
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (deleteDialog.userId) {
      try {
        await apiService.deleteUser(deleteDialog.userId);
        removeUser(deleteDialog.userId);
        setDeleteDialog({ open: false, userId: null });
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to delete user');
      }
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      skill_level: 3,
      elo_rating: undefined,
      preferred_side: 'right',
      is_active: true,
      is_admin: false,
      club_id: undefined,
    });
    setEditingUser(null);
    setFormErrors({});
  };

  // Ensure users is always an array before filtering
  const safeUsers = Array.isArray(users) ? users : [];
  const filteredUsers = safeUsers.filter((u) => {
    if (filters.status !== 'all') {
      if (filters.status === 'active' && !u.is_active) return false;
      if (filters.status === 'inactive' && u.is_active) return false;
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!u.name.toLowerCase().includes(search) && !u.email.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'avatar',
      headerName: '',
      width: 56,
      sortable: false,
      renderCell: (params) => (
        <Avatar
          src={getAvatarUrl(params.row.avatar_url) || undefined}
          sx={{ width: 36, height: 36 }}
        >
          {isDefaultAvatar(params.row.avatar_url) ? params.row.name?.charAt(0)?.toUpperCase() || '?' : null}
        </Avatar>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'elo_rating',
      headerName: 'ELO Rating',
      width: 120,
      renderCell: (params) => {
        const elo = params.value ?? params.row.initial_elo;
        return elo ? (elo as number).toFixed(1) : 'N/A';
      },
    },
    {
      field: 'initial_elo',
      headerName: 'Initial ELO',
      width: 120,
      renderCell: (params) => {
        return params.value ? (params.value as number).toFixed(1) : 'N/A';
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <StatusChip status={params.value ? 'active' : 'inactive'} />
      ),
    },
    {
      field: 'total_matches',
      headerName: 'Matches',
      width: 100,
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'win_rate',
      headerName: 'Win Rate',
      width: 120,
      renderCell: (params) => `${(params.value || 0).toFixed(1)}%`,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Person />}
          label={t('adminUsers.view', 'View')}
          onClick={() => navigate(`/admin/users/${params.row.id}`)}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label={t('adminUserProgress.viewProgress')}
          onClick={() => navigate(`/admin/users/${params.row.id}/progress`)}
        />,
        <GridActionsCellItem
          icon={<Edit />}
          label={t('adminUsers.edit')}
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => setDeleteDialog({ open: true, userId: params.row.id })}
          showInMenu
        />,
      ],
    },
  ];

  if (!currentUser?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">{t('adminUsers.accessDenied')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          {t('adminUsers.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setOpenDialog(true);
          }}
        >
          {t('adminUsers.addUser')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FilterBar
        searchValue={filters.search}
        onSearchChange={(value) => setFilters({ search: value })}
        filters={[
          {
            label: t('adminUsers.status'),
            value: filters.status,
            options: [
              { label: t('adminUsers.all'), value: 'all' },
              { label: t('adminUsers.active'), value: 'active' },
              { label: t('adminUsers.inactive'), value: 'inactive' },
            ],
            onChange: (value) => setFilters({ status: value as any }),
          },
        ]}
        onClear={() => setFilters({ status: 'all', search: '' })}
      />

      <DataTable
        rows={filteredUsers}
        columns={columns}
        loading={loading}
        pageSize={25}
      />

      <FormDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        title={editingUser ? t('adminUsers.editUser') : t('adminUsers.createUser')}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label={t('adminUsers.name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!formErrors.name}
            helperText={formErrors.name}
            required
          />
          <TextField
            fullWidth
            label={t('adminUsers.email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!formErrors.email}
            helperText={formErrors.email}
            required
          />
          <TextField
            fullWidth
            label={editingUser ? t('adminUsers.newPassword') : t('adminUsers.password')}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={!!formErrors.password}
            helperText={formErrors.password}
            required={!editingUser}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label={t('adminUsers.skillLevel')}
              type="number"
              inputProps={{ min: 1, max: 10 }}
              value={formData.skill_level}
              onChange={(e) => setFormData({ ...formData, skill_level: parseInt(e.target.value) || 3 })}
              error={!!formErrors.skill_level}
              helperText={formErrors.skill_level}
            />
            {editingUser && (
              <TextField
                fullWidth
                label={t('adminUsers.eloOverride')}
                type="number"
                inputProps={{ min: 1, max: 10, step: 0.1 }}
                value={formData.elo_rating ?? ''}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setFormData({
                    ...formData,
                    elo_rating: e.target.value === '' ? undefined : (isNaN(val) ? formData.elo_rating : Math.min(10, Math.max(1, val))),
                  });
                }}
                error={!!formErrors.elo_rating}
                helperText={formErrors.elo_rating || t('adminUsers.eloHelper')}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>{t('adminUsers.preferredSide')}</InputLabel>
              <Select
                value={formData.preferred_side}
                label={t('adminUsers.preferredSide')}
                onChange={(e) => setFormData({ ...formData, preferred_side: e.target.value })}
              >
                <MenuItem value="left">{t('adminUsers.left')}</MenuItem>
                <MenuItem value="right">{t('adminUsers.right')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {clubs.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t('adminUsers.club')}</InputLabel>
              <Select
                value={formData.club_id || ''}
                label={t('adminUsers.club')}
                onChange={(e) => setFormData({ ...formData, club_id: e.target.value ? Number(e.target.value) : undefined })}
              >
                <MenuItem value="">{t('adminUsers.none')}</MenuItem>
                {clubs.map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label={t('adminUsers.active')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
              }
              label={t('adminUsers.admin')}
            />
          </Box>
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null })}
        onConfirm={handleDelete}
        title={t('adminUsers.deleteUser')}
        message={t('adminUsers.deleteConfirm')}
        confirmLabel={t('adminUsers.confirmDelete')}
        severity="error"
      />
    </Box>
  );
};
